import type { SuppressionRule } from '@bloo-team/api-client'
import { termsSummary } from './suppression'
import { entityKey, type Signal, type SignalEntity } from './signal-model'
import { useSignals, type SignalsScope } from './signals'

/**
 * Entity context (docs/signals-requirements.md §5 "Entity flyout", decisions
 * S8 / S15): 30-day history and prevalence of one entity, derived
 * client-side over the 30d baseline. The baseline is the same react-query
 * list `useSignals(scope, '30d')` serves the queue, so opening the flyout
 * costs no extra request once the 30d window has been fetched once.
 *
 * Everything but the hook is pure over Signal[] — the flyout is a view.
 */

const DAY = 86_400_000
/** Buckets in `entityHistory().daily`. */
export const HISTORY_DAYS = 30

/** The 30-day baseline every entity stat reads from. */
export function useSignalBaseline(scope: SignalsScope) {
  return useSignals(scope, '30d')
}

export interface EntityHistory {
  /** ms epoch of the oldest signal in the baseline, null when none. */
  first: number | null
  last: number | null
  count30d: number
  /** Top 5 detections by signal count, most frequent first. */
  detections: { name: string; count: number }[]
  /** Distinct host entity values co-occurring on the same signals (the entity itself excluded). */
  hostsSeenWith: string[]
  /** Signals per day, HISTORY_DAYS buckets, oldest → newest (index 29 = today). */
  daily: number[]
}

const isHost = (e: SignalEntity) => e.field.toLowerCase() === 'host'

function hasEntity(s: Signal, key: string): boolean {
  return s.entities.some((e) => entityKey(e) === key)
}

/**
 * History of one entity (signal-model `entityKey`) over the baseline.
 * `now` anchors the daily buckets so tests are deterministic.
 */
export function entityHistory(key: string, signals: Signal[], now = Date.now()): EntityHistory {
  let first: number | null = null
  let last: number | null = null
  let count30d = 0
  const byDetection = new Map<string, number>()
  const hosts = new Set<string>()
  const daily = new Array<number>(HISTORY_DAYS).fill(0)
  for (const s of signals) {
    if (!hasEntity(s, key)) continue
    count30d++
    if (first === null || s.at < first) first = s.at
    if (last === null || s.at > last) last = s.at
    const name = s.detectionName || '—'
    byDetection.set(name, (byDetection.get(name) ?? 0) + 1)
    for (const e of s.entities) if (isHost(e) && entityKey(e) !== key) hosts.add(e.value)
    const age = Math.floor((now - s.at) / DAY)
    if (age >= 0 && age < HISTORY_DAYS) daily[HISTORY_DAYS - 1 - age]!++
  }
  const detections = [...byDetection.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
    .slice(0, 5)
  return { first, last, count30d, detections, hostsSeenWith: [...hosts].sort(), daily }
}

export interface Prevalence {
  /** Distinct host values on signals carrying this entity (a host entity counts itself). */
  hosts: number
  /** Distinct hosts across the whole baseline. */
  total: number
  /** hosts / total; null when the baseline has no hosts. */
  share: number | null
  /** share < 0.1 over a baseline of ≥ 10 hosts; null when the baseline is too small to judge. */
  rare: boolean | null
}

/** Minimum distinct hosts in the baseline before "rare" means anything. */
export const RARE_MIN_HOSTS = 10
export const RARE_SHARE = 0.1

/** How widely an entity is seen across hosts in the baseline. */
export function prevalence(field: string, value: string, signals: Signal[]): Prevalence {
  const key = entityKey({ field, value })
  const all = new Set<string>()
  const withEntity = new Set<string>()
  for (const s of signals) {
    const has = hasEntity(s, key)
    for (const e of s.entities) {
      if (!isHost(e)) continue
      all.add(e.value.toLowerCase())
      if (has) withEntity.add(e.value.toLowerCase())
    }
  }
  const total = all.size
  const hosts = withEntity.size
  const share = total ? hosts / total : null
  const rare = total >= RARE_MIN_HOSTS && share !== null ? share < RARE_SHARE : null
  return { hosts, total, share, rare }
}

/** Signals carrying the entity, newest first, capped. */
export function recentWithEntity(key: string, signals: Signal[], limit = 8): Signal[] {
  return signals
    .filter((s) => hasEntity(s, key))
    .sort((a, b) => b.at - a.at)
    .slice(0, limit)
}

export interface MatchingRule {
  id: string
  summary: string
}

/**
 * Suppression rules whose every term (AND of keys) matches one of the given
 * entities (OR within a key). Term keys are the legacy concatenation
 * `SuspectHost`; the match is case-insensitive on both key and value.
 */
export function matchingRules(entities: SignalEntity[], rules: SuppressionRule[]): MatchingRule[] {
  const have = new Map<string, Set<string>>()
  for (const e of entities) {
    const k = `${e.role}${e.field}`.toLowerCase()
    const set = have.get(k) ?? new Set<string>()
    set.add(e.value.toLowerCase())
    have.set(k, set)
  }
  return rules
    .filter((r) => {
      const terms = Object.entries(r.rule)
      return (
        terms.length > 0 &&
        terms.every(([k, vals]) => {
          const set = have.get(k.toLowerCase())
          return set !== undefined && vals.some((x) => set.has(x.toLowerCase()))
        })
      )
    })
    .map((r) => ({ id: r.rule_id, summary: `${r.name || r.wb_id}: ${termsSummary(r.rule)}` }))
}

/**
 * Per-entity inline context for the panel chips: `{ count30d, first, rare }`
 * keyed by entityKey, over the baseline.
 */
export function entityContextFor(
  entities: SignalEntity[],
  baseline: Signal[],
  now = Date.now(),
): Record<string, { count30d: number; first: number | null; rare: boolean | null }> {
  const out: Record<string, { count30d: number; first: number | null; rare: boolean | null }> = {}
  for (const e of entities) {
    const key = entityKey(e)
    if (out[key]) continue
    const h = entityHistory(key, baseline, now)
    out[key] = { count30d: h.count30d, first: h.first, rare: prevalence(e.field, e.value, baseline).rare }
  }
  return out
}
